import React, { useEffect, useRef } from 'react'

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  color: string
  alpha: number
  pulseSpeed: number
}

export const AuthParticlesCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animationFrameId: number
    let width = (canvas.width = window.innerWidth)
    let height = (canvas.height = window.innerHeight)

    const handleResize = () => {
      if (!canvas) return
      width = canvas.width = window.innerWidth
      height = canvas.height = window.innerHeight
    }

    window.addEventListener('resize', handleResize)

    // Interactive mouse coordinates
    const mouse = { x: -1000, y: -1000, radius: 140 }
    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX
      mouse.y = e.clientY
    }
    const handleMouseLeave = () => {
      mouse.x = -1000
      mouse.y = -1000
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseleave', handleMouseLeave)

    // Palette: Warm Cream, Champagne Gold, Royal Navy Sapphire, Light Cyan
    const particleColors = [
      'rgba(247, 237, 218, ', // Warm Cream
      'rgba(223, 204, 168, ', // Champagne Gold
      'rgba(79, 117, 254, ',  // Royal Sapphire
      'rgba(56, 189, 248, ',  // Cyan Highlight
    ]

    const particleCount = Math.min(Math.floor((width * height) / 16000), 85)
    const particles: Particle[] = []

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.65,
        vy: (Math.random() - 0.5) * 0.65,
        size: Math.random() * 2.4 + 1.2,
        color: particleColors[Math.floor(Math.random() * particleColors.length)],
        alpha: Math.random() * 0.5 + 0.35,
        pulseSpeed: Math.random() * 0.02 + 0.01,
      })
    }

    let tick = 0

    const render = () => {
      tick += 1
      ctx.clearRect(0, 0, width, height)

      // Update and draw particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]

        p.x += p.vx
        p.y += p.vy

        // Wrap around canvas edges
        if (p.x < 0) p.x = width
        if (p.x > width) p.x = 0
        if (p.y < 0) p.y = height
        if (p.y > height) p.y = 0

        // Gentle breathing pulse
        const currentAlpha = p.alpha + Math.sin(tick * p.pulseSpeed) * 0.15
        const safeAlpha = Math.max(0.12, Math.min(0.88, currentAlpha))

        // Draw particle glowing dot
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fillStyle = `${p.color}${safeAlpha})`
        ctx.shadowBlur = 10
        ctx.shadowColor = `${p.color}0.7)`
        ctx.fill()
        ctx.shadowBlur = 0

        // Draw connecting constellation lines between nearby particles
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j]
          const dx = p.x - p2.x
          const dy = p.y - p2.y
          const dist = Math.sqrt(dx * dx + dy * dy)

          if (dist < 125) {
            const lineAlpha = (1 - dist / 125) * 0.22
            ctx.beginPath()
            ctx.moveTo(p.x, p.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.strokeStyle = `rgba(235, 218, 185, ${lineAlpha})`
            ctx.lineWidth = 0.85
            ctx.stroke()
          }
        }

        // Draw interactive line connection to mouse cursor
        const mdx = p.x - mouse.x
        const mdy = p.y - mouse.y
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy)
        if (mdist < mouse.radius) {
          const mLineAlpha = (1 - mdist / mouse.radius) * 0.45
          ctx.beginPath()
          ctx.moveTo(p.x, p.y)
          ctx.lineTo(mouse.x, mouse.y)
          ctx.strokeStyle = `rgba(247, 237, 218, ${mLineAlpha})`
          ctx.lineWidth = 1.2
          ctx.stroke()
        }
      }

      animationFrameId = requestAnimationFrame(render)
    }

    render()

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseleave', handleMouseLeave)
      cancelAnimationFrame(animationFrameId)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className='anime-particles-canvas'
    />











  )
}
